import { SubmissionError } from "redux-form";
import * as Sentry from "@sentry/react";
import axios from "axios";
import _ from "underscore";

import {
  CTA_CLICKED_EVENT,
  FETCH_INDUSTRIES_SUCCESS,
  FETCH_POPULAR_LOCATION_OPTIONS_SUCCESS,
  FORCE_PAGE_REMOUNT,
  HIDE_LEFT_SIDEBAR,
  SET_ACTIVE_MESSENGER_ROOM,
  SET_LAYOUT_BG_COLOR,
  SHOW_LEFT_SIDEBAR,
} from "@js/apps/common/action-types";
import { Events } from "@js/services/analytics";
import type { AppThunkAction } from "@js/store";
import type { FilterAppliedPayload } from "@js/types/common";

export type CtaClickedActionTypes = {
  ctaName: EnumType<typeof ENUMS.UserClicksEventCTAName>;
  ctaLocation: EnumType<typeof ENUMS.UserClicksEventCTALocation>;
};

export const CtaClickedAction = (params: CtaClickedActionTypes) => {
  return {
    type: CTA_CLICKED_EVENT,
    payload: params,
  };
};

export const forcePageRemount = () => ({
  type: FORCE_PAGE_REMOUNT,
});

export const showLeftSidebar = () => ({
  type: SHOW_LEFT_SIDEBAR,
});

export const hideLeftSidebar = () => ({
  type: HIDE_LEFT_SIDEBAR,
});

export const setLayoutBackgroundColor = (color: string | undefined) => ({
  type: SET_LAYOUT_BG_COLOR,
  payload: color,
});

const _searchTaxonomyItems = (
  phrase: string,
  endpoint: string,
  resolve: (value: unknown) => void,
  params?: Record<string, unknown>,
) =>
  axios
    .get(`/api/${endpoint}/?search=${encodeURIComponent(phrase)}`, { params })
    .then((response) => {
      resolve(response.data);
    });

const _searchTaxonomyItemsDebounce = _.debounce(_searchTaxonomyItems, 300);

type SearchTaxonomyItemsArgs = {
  phrase: string;
  endpoint: string;
  immediate?: boolean;
  params?: Record<string, unknown>;
};

export const searchTaxonomyItems = ({
  phrase,
  endpoint,
  immediate,
  params,
}: SearchTaxonomyItemsArgs) =>
  new Promise((resolve) => {
    void (immediate ? _searchTaxonomyItems : _searchTaxonomyItemsDebounce)(
      phrase,
      endpoint,
      resolve,
      params,
    );
  });

export const getTaxonomyItem = (id: number, endpoint: string) =>
  new Promise((resolve) =>
    axios
      .get(`/api/${endpoint}/${id}/`)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) =>
        Sentry.captureException("GetTaxonomyItemException", {
          extra: { error },
        }),
      ),
  );

export const createTaxonomyItem =
  (name: string, endpoint: string, fieldName: string) => () =>
    new Promise((resolve, reject) =>
      axios
        .post(`/api/${endpoint}/`, { name })
        .then((response) => resolve(response.data))
        .catch(({ response: { data = {} } }) => {
          let submissionError: Record<string, string> = {};

          if (fieldName && data.name) {
            submissionError[fieldName] = data.name;
          } else {
            submissionError = data;
          }

          return reject(new SubmissionError(submissionError));
        }),
    );

export const fetchIndustries = (): AppThunkAction<void> => (dispatch) =>
  new Promise((resolve) =>
    axios.get("/api/industries/").then((response) => {
      dispatch({
        type: FETCH_INDUSTRIES_SUCCESS,
        payload: response.data,
      });

      resolve(response.data);
    }),
  );

/*
 *   @param id - id of the entity that was set as `type`
 *   @param type - one of ENUMS.VisitsCounterType
 */
export const updateVisitsCounter = (
  id: number,
  type: EnumType<typeof ENUMS.VisitsCounterType>,
) =>
  new Promise((resolve) => {
    return axios
      .get(`/api/visits_counter/?id=${id}&type=${type}`)
      .then(resolve)
      .catch(() => {
        // ignore
      });
  });

export const confirmAction = (data: Record<string, unknown>) =>
  new Promise((resolve, reject) =>
    axios
      .post("/api/confirm/", data)
      .then((response) => resolve(response.data))
      .catch((error) => reject(error.response.data)),
  );

export const fetchPopularLocationOptions =
  (): AppThunkAction<Promise<void>> => (dispatch) =>
    new Promise((resolve) =>
      axios.get(`/api/frequent_talent_location_filters/`).then((response) => {
        dispatch({
          type: FETCH_POPULAR_LOCATION_OPTIONS_SUCCESS,
          payload: response.data,
        });

        resolve(response.data);
      }),
    );

export const setActiveMessengerRoom = (roomId: number | undefined) => ({
  type: SET_ACTIVE_MESSENGER_ROOM,
  payload: { roomId },
});

export const signUpAttempt = (amplitude_device_id: string) =>
  axios.post(`/api/users/signup_attempt/`, {
    amplitude_device_id: amplitude_device_id,
  });

export const filterApplied = (payload: FilterAppliedPayload) => ({
  type: Events.FILTER_SEARCH,
  payload,
});
